import { isWhitelistedPerform3Account } from '@common/login/selectors';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { getLocale } from '../../common/lang/selectors';
import { LangStatePart } from '../../setup/types';
import { LoginState } from './../../common/login/reducer';

export type Settings = { [key: string]: unknown };

export enum Unit {
    metric = 'METRIC',
    imperial = 'IMPERIAL',
}

export type SettingState = {
    settings: Settings;
} & {
    includeCruiseControlInRating: boolean;
    enablePerform3: boolean;
    isCalcParamsDialogVisible: boolean;
    unit: Unit;
    isUnitLoading: boolean;
    calculationParams: {
        currentPreset: CalculationParamsPreset;
        currentCalcParams: CalculationParams;
        currentCalcParamsUpdatedAt: Date;
        savedPreset: CalculationParamsPreset;
        savedCalcParams: CalculationParams;
        savedCalcParamsUpdatedAt: Date;
        currentOverspeedLimit: number;
        savedOverspeedLimit: number;
    };
};

export type CalculationParamObject = {
    enabled: boolean;
    weight: number;
};

export enum CalculationParamsPreset {
    none = 'none',
    'mixed-fleet' = 'mixed-fleet',
}

export type CalculationParams = {
    coastingRating: CalculationParamObject;
    accelerationPedalRating: CalculationParamObject;
    brakingRating: CalculationParamObject;
    cruiseControlRating: CalculationParamObject;
    overSpeedRating: CalculationParamObject;
    harshAccelerationRating: CalculationParamObject;
    harshBrakingRating: CalculationParamObject;
    excessiveIdlingRating: CalculationParamObject;
    electricAccelerationPedalRating: CalculationParamObject;
    electricCruiseControlRating: CalculationParamObject;
    electricOverSpeedRating: CalculationParamObject;
    recuperationRating: CalculationParamObject;
};

export type CalculationParamsWithMetadata = CalculationParams & {
    updatedAt: Date;
};

export type ElectricCalculationParams = {
    coastingRating: CalculationParamObject;
    electricAccelerationPedalRating: CalculationParamObject;
    brakingRating: CalculationParamObject;
    electricCruiseControlRating: CalculationParamObject;
    electricOverSpeedRating: CalculationParamObject;
    recuperationRating: CalculationParamObject;
};

export type FuelCalculationParams = {
    coastingRating: CalculationParamObject;
    accelerationPedalRating: CalculationParamObject;
    brakingRating: CalculationParamObject;
    cruiseControlRating: CalculationParamObject;
    overSpeedRating: CalculationParamObject;
    harshAccelerationRating: CalculationParamObject;
    harshBrakingRating: CalculationParamObject;
    excessiveIdlingRating: CalculationParamObject;
};

export const defaultCalculationParamsPreset = CalculationParamsPreset.none;

export const defaultCalculationParams: CalculationParams = {
    coastingRating: { enabled: true, weight: 20 },
    accelerationPedalRating: { enabled: true, weight: 20 },
    brakingRating: { enabled: true, weight: 20 },
    cruiseControlRating: { enabled: true, weight: 20 },
    overSpeedRating: { enabled: true, weight: 20 },
    harshAccelerationRating: { enabled: true, weight: 0 },
    harshBrakingRating: { enabled: true, weight: 0 },
    excessiveIdlingRating: { enabled: true, weight: 0 },
    electricAccelerationPedalRating: { enabled: true, weight: 25 },
    electricCruiseControlRating: { enabled: true, weight: 25 },
    electricOverSpeedRating: { enabled: true, weight: 25 },
    recuperationRating: { enabled: true, weight: 25 },
};

export const defaultCalculationParamsUpdatedAt = new Date('2020-01-01T00:00:00Z');

export const defaultOverspeedLimit = 86;

export const defaultState: SettingState = {
    settings: {},
    includeCruiseControlInRating: true,
    enablePerform3: true,
    isCalcParamsDialogVisible: false,
    unit: Unit.metric,
    isUnitLoading: false,
    calculationParams: {
        currentPreset: CalculationParamsPreset.none,
        currentCalcParams: defaultCalculationParams,
        currentCalcParamsUpdatedAt: defaultCalculationParamsUpdatedAt,
        savedPreset: CalculationParamsPreset.none,
        savedCalcParams: defaultCalculationParams,
        savedCalcParamsUpdatedAt: defaultCalculationParamsUpdatedAt,
        currentOverspeedLimit: defaultOverspeedLimit,
        savedOverspeedLimit: defaultOverspeedLimit,
    },
};

const settingSlice = createSlice({
    name: 'settings',
    initialState: defaultState,
    reducers: {
        toggleCalcParamsDialog(state, { payload }: PayloadAction<boolean>) {
            state.isCalcParamsDialogVisible = payload;
        },
        setCurrentCalculationParams(
            state,
            {
                payload: { updatedCalcParams },
            }: {
                payload: {
                    previousSavedCalcParams: CalculationParams;
                    updatedCalcParams: CalculationParams;
                    previousSavedPreset: CalculationParamsPreset;
                    updatedPreset: CalculationParamsPreset;
                    previousSavedOverspeedLimit: number;
                    updatedOverspeedLimit: number;
                    restored: boolean;
                };
            },
        ) {
            state.calculationParams.currentCalcParams = updatedCalcParams;
        },
        setSavedCalculationParams(
            state,
            { payload: { value } }: { payload: { value: CalculationParams } },
        ) {
            state.calculationParams.savedCalcParams = value;
        },
        setCurrentCalculationParamsPreset(
            state,
            { payload: { value } }: { payload: { value: CalculationParamsPreset } },
        ) {
            state.calculationParams.currentPreset = value;
        },
        setSavedCalculationParamsPreset(
            state,
            { payload: { value } }: { payload: { value: CalculationParamsPreset } },
        ) {
            state.calculationParams.savedPreset = value;
        },
        setCurrentOverspeedLimit(state, { payload: { value } }: { payload: { value: number } }) {
            state.calculationParams.currentOverspeedLimit = value;
        },
        setSavedOverspeedLimit(state, { payload: { value } }: { payload: { value: number } }) {
            state.calculationParams.savedOverspeedLimit = value;
        },
        setSavedCalculationParamsUpdatedAt(
            state,
            { payload: { value } }: { payload: { value: Date } },
        ) {
            state.calculationParams.savedCalcParamsUpdatedAt = value;
        },
        setIncludeCruiseControlInRating(
            state,
            { payload: { value } }: { payload: { value: boolean } },
        ) {
            state.includeCruiseControlInRating = value;
        },
        setEnablePerform3(state, { payload }) {
            state.enablePerform3 = payload;
        },
        setUnit(state, { payload: { value } }: { payload: { value: Unit } }) {
            state.unit = value;
        },
        setUnitLoading(state, { payload }: PayloadAction<boolean>) {
            state.isUnitLoading = payload;
        },
        updateSavingFailed() {},
        loadSettings() {},
        loadSettingsFailed() {},
        loadSettingsSuccess(state, { payload: { settings } }: { payload: { settings: Settings } }) {
            state.settings = settings;
        },
        updateSetting(
            state,
            { payload: { key, value } }: { payload: { key: string; value: unknown } },
        ) {
            state.settings[key] = value;
        },
        saveUnitSetting(state, { payload: { value } }: { payload: { value: Unit } }) {
            state.unit = value;
        },
    },
});

export const {
    updateSetting,
    loadSettings,
    loadSettingsSuccess,
    loadSettingsFailed,
    updateSavingFailed,
    setIncludeCruiseControlInRating,
    setEnablePerform3,
    setUnit,
    setUnitLoading,
    setCurrentCalculationParams,
    setSavedCalculationParams,
    setCurrentCalculationParamsPreset,
    setSavedCalculationParamsPreset,
    setCurrentOverspeedLimit,
    setSavedOverspeedLimit,
    setSavedCalculationParamsUpdatedAt,
    toggleCalcParamsDialog,
    saveUnitSetting,
} = settingSlice.actions;
export default settingSlice.reducer;

export const includeCruiseControlInRating = (state: { settings: SettingState }) =>
    _.get(state, 'settings.includeCruiseControlInRating', true);

export const enablePerform3 = (state: { settings: SettingState; login: LoginState }) => {
    if (!isWhitelistedPerform3Account(state)) return true;
    return _.get(state, 'settings.enablePerform3', true);
};

export const isCalcParamsDialogVisible = (state: { settings: SettingState }) =>
    _.get(state, 'settings.isCalcParamsDialogVisible', false);

export const currentCalculationParams = (state: { settings: SettingState }) =>
    _.get(state, 'settings.calculationParams.currentCalcParams');

export const savedCalculationParams = (state: { settings: SettingState }) =>
    _.get(state, 'settings.calculationParams.savedCalcParams');

export const currentCalculationParamsUpdatedAt = (state: { settings: SettingState }) =>
    _.get(
        state,
        'settings.calculationParams.currentCalcParamsUpdatedAt',
        defaultCalculationParamsUpdatedAt,
    );

export const savedCalculationParamsUpdatedAt = (state: { settings: SettingState }) =>
    _.get(
        state,
        'settings.calculationParams.savedCalcParamsUpdatedAt',
        defaultCalculationParamsUpdatedAt,
    );

export const currentCalculationParamsPreset = (state: { settings: SettingState }) =>
    _.get(state, 'settings.calculationParams.currentPreset', CalculationParamsPreset.none);

export const savedCalculationParamsPreset = (state: { settings: SettingState }) =>
    _.get(state, 'settings.calculationParams.savedPreset', CalculationParamsPreset.none);

export const currentOverspeedLimit = (state: { settings: SettingState }) =>
    _.get(state, 'settings.calculationParams.currentOverspeedLimit', defaultOverspeedLimit);

export const savedOverspeedLimit = (state: { settings: SettingState }) =>
    _.get(state, 'settings.calculationParams.savedOverspeedLimit', defaultOverspeedLimit);

export const currentUnit = (state: { settings: SettingState } & LangStatePart) => {
    const defaultUnit = getLocale(state) === 'en-GB' ? Unit.imperial : Unit.metric;
    return _.get(state, 'settings.unit', defaultUnit);
};

export const setUnitSetting = (unit: Unit) => saveUnitSetting({ value: unit });

export const isUnitLoading = (state: { settings: SettingState }) =>
    _.get(state, 'settings.isUnitLoading', false);
